import { useState } from 'react'
import { useAbm } from '../hooks/useAbm'
import AbmTable from '../components/AbmTable'
import Modal from '../components/Modal'
import api from '../api/client'
import Boton from '../components/Boton'

// El vínculo de Telegram NO vive en el form: Guardar jamás lo toca (pisaba el
// chat recién vinculado). Se muestra desde la fila y se quita con Desvincular.
const EMPTY = { nombre: '', telefono: '', vehiculo: '', foto_url: '', activo: true, interno: false, externo: false }

export default function FleterosPage() {
  const { rows, loading, create, update, remove } = useAbm('fleteros')
  const [modal, setModal] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [vinculo, setVinculo] = useState(null)      // código de vínculo de Telegram generado
  const [genVinculo, setGenVinculo] = useState(false)
  const [rastreo, setRastreo] = useState(null)      // URL fija de rastreo del teléfono de la camioneta
  const [genRastreo, setGenRastreo] = useState(false)
  // El fletero que además es usuario de LGR (Odello): su Telegram ya está en su
  // usuario y el bot no acepta el código de fletero desde ese chat. Se elige acá.
  const [usuariosTg, setUsuariosTg] = useState(null)
  const [usuarioTg, setUsuarioTg] = useState('')

  const openCreate = () => { setForm(EMPTY); setErr(''); setModal('new') }
  const openEdit = (row) => { setUsuariosTg(null); setUsuarioTg(''); setForm({ nombre: row.nombre, telefono: row.telefono || '', vehiculo: row.vehiculo || '', foto_url: row.foto_url || '', activo: row.activo, interno: !!row.interno, externo: !!row.externo }); setErr(''); setVinculo(null); setRastreo(null); setModal(row) }

  // Telegram (27 ago 2026): el fletero no tiene login — su canal es el chat.
  // Vinculado, al despachar le llega solo el link de la hoja de ruta.
  const generarVinculo = async () => {
    setGenVinculo(true)
    try {
      const { data } = await api.post(`/fleteros/${modal.id}/vincular-telegram`)
      setVinculo(data)
    } catch {
      setVinculo({ error: 'No se pudo generar el código.' })
    } finally { setGenVinculo(false) }
  }

  const abrirUsuariosTg = async () => {
    try {
      const { data } = await api.get('/fleteros-telegram/usuarios')
      setUsuariosTg(data)
    } catch {
      setUsuariosTg([])
    }
  }

  const vincularConUsuario = async () => {
    setGenVinculo(true)
    try {
      const { data } = await api.post(`/fleteros/${modal.id}/vincular-telegram-usuario`, { usuario_id: Number(usuarioTg) })
      setModal(m => ({ ...m, telegram_chat_id: data.telegram_chat_id }))
      setUsuariosTg(null)
    } catch (error) {
      setVinculo({ error: error.response?.data?.message || 'No se pudo vincular.' })
    } finally { setGenVinculo(false) }
  }

  // Rastreo de flota (1 sep 2026): URL fija que se pega UNA vez en una app GPS
  // de background en el teléfono de la camioneta — reporta solo, sin que el
  // repartidor toque nada. Idempotente: siempre devuelve la misma URL.
  const verRastreo = async () => {
    setGenRastreo(true)
    try {
      const { data } = await api.post(`/fleteros/${modal.id}/token-rastreo`)
      setRastreo(data)
      try { await navigator.clipboard.writeText(data.url) } catch { /* sin permiso: queda a la vista */ }
    } catch {
      setRastreo({ error: 'No se pudo obtener la URL de rastreo.' })
    } finally { setGenRastreo(false) }
  }

  // Desvincular es acción explícita e inmediata, no un campo del form.
  const desvincular = async () => {
    if (!window.confirm('¿Desvincular el Telegram de este fletero?')) return
    try {
      await update(modal.id, { telegram_chat_id: null })
      setModal(m => ({ ...m, telegram_chat_id: null }))
    } catch {
      setErr('No se pudo desvincular.')
    }
  }

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErr('')
    try {
      if (modal === 'new') await create(form)
      else await update(modal.id, form)
      setModal(null)
    } catch (error) {
      setErr(error.response?.data?.message || 'Error al guardar')
    } finally { setSaving(false) }
  }

  const field = (key, label, type = 'text') => (
    <div key={key}>
      <label style={styles.label}>{label}</label>
      <input style={styles.input} type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
    </div>
  )

  const columns = [
    { key: 'nombre', label: 'Nombre' },
    { key: 'interno', label: 'Tipo', render: (v, row) => v ? 'Interno (retira)' : row.es_andreani ? 'Andreani' : row.externo ? 'Externo (cobra por salida)' : 'Reparto' },
    { key: 'telefono', label: 'Teléfono' },
    { key: 'vehiculo', label: 'Vehículo' },
    { key: 'activo', label: 'Activo', render: v => v ? 'Sí' : 'No' },
  ]

  return (
    <div>
      <AbmTable title="Fleteros" columns={columns} rows={rows} loading={loading} onCreate={openCreate} onEdit={openEdit} onDelete={remove} />
      {modal && (
        <Modal title={modal === 'new' ? 'Nuevo Fletero' : 'Editar Fletero'} onClose={() => setModal(null)}>
          <form onSubmit={handleSave} style={styles.form}>
            {field('nombre', 'Nombre *')}
            {field('telefono', 'Teléfono')}
            {field('vehiculo', 'Vehículo')}
            {field('foto_url', 'Foto (URL) — se muestra al cliente en el seguimiento')}
            <label style={styles.checkRow}>
              <input type="checkbox" checked={form.interno} onChange={e => setForm(f => ({ ...f, interno: e.target.checked }))} />
              Canal interno (retira en depósito/sucursal — no requiere zona ni hoja de ruta)
            </label>
            <label style={styles.checkRow}>
              <input type="checkbox" checked={form.externo} onChange={e => setForm(f => ({ ...f, externo: e.target.checked }))} />
              Externo (cobra por salida — al armarle una salida se carga lo que cobra)
            </label>
            <label style={styles.checkRow}>
              <input type="checkbox" checked={form.activo} onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))} />
              Activo
            </label>
            {modal !== 'new' && !form.interno && (
              <div style={styles.vinculoBox}>
                <label style={styles.label}>Telegram</label>
                {modal.telegram_chat_id ? (
                  <p style={styles.hint}>
                    ✅ Vinculado (chat {modal.telegram_chat_id}). Al despachar una salida le llega el link de la hoja de ruta.{' '}
                    <button type="button" style={styles.linkBtn} onClick={desvincular}>Desvincular</button>
                  </p>
                ) : (
                  <p style={styles.hint}>Sin vincular: el link de cada viaje se sigue copiando a mano.</p>
                )}
                <Boton variante="secondary" sm type="button" onClick={generarVinculo} disabled={genVinculo}>
                  {genVinculo ? 'Generando…' : '🔗 Generar código de vínculo'}
                </Boton>
                {vinculo?.codigo && (
                  <div style={styles.vinculoOk}>
                    Código: <strong style={{ fontFamily: 'monospace', fontSize: '1.05rem' }}>{vinculo.codigo}</strong><br />
                    El fletero le escribe a <strong>@{vinculo.bot}</strong> en Telegram y manda: <code>/vincular {vinculo.codigo}</code>
                    <br /><span style={{ color: '#64748b' }}>Vence en 10 minutos. Al vincularse, el chat se carga solo (cerrá y volvé a abrir la ficha para verlo).</span>
                  </div>
                )}
                {!modal.telegram_chat_id && usuariosTg === null && (
                  <div style={{ marginTop: 6 }}>
                    <button type="button" style={styles.linkBtn} onClick={abrirUsuariosTg}>¿El fletero es un usuario de LGR? Usar el Telegram de un usuario de LGR</button>
                  </div>
                )}
                {!modal.telegram_chat_id && usuariosTg !== null && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                    <select id="fletero-usuario-tg" style={{ ...styles.input, width: 'auto', minWidth: 220 }} value={usuarioTg} onChange={e => setUsuarioTg(e.target.value)}>
                      <option value="">Elegí el usuario…</option>
                      {usuariosTg.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
                    </select>
                    <Boton variante="secondary" sm type="button" onClick={vincularConUsuario} disabled={!usuarioTg || genVinculo}>Usar su Telegram</Boton>
                    {usuariosTg.length === 0 && <span style={styles.hint}>No hay usuarios con Telegram vinculado.</span>}
                  </div>
                )}
                {vinculo?.error && <p style={styles.error}>{vinculo.error}</p>}
              </div>
            )}
            {modal !== 'new' && !form.interno && (
              <div style={styles.vinculoBox}>
                <label style={styles.label}>Rastreo del teléfono de la camioneta</label>
                <p style={styles.hint}>
                  URL fija para una app GPS en background (ej. GPSLogger): se configura una sola vez en el
                  teléfono y reporta la posición todo el día, sin que el repartidor autorice nada. La posición
                  cae en las salidas en reparto del fletero y arma el recorrido en el mapa.
                </p>
                <Boton variante="secondary" sm type="button" onClick={verRastreo} disabled={genRastreo}>
                  {genRastreo ? 'Obteniendo…' : '📡 URL de rastreo (copiar)'}
                </Boton>
                {rastreo?.url && (
                  <div style={styles.vinculoOk}>
                    Copiada al portapapeles:<br />
                    <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{rastreo.url}</code><br />
                    <span style={{ color: '#64748b' }}>
                      En la app, agregá la posición como parámetros: <code>?lat=%LAT&lon=%LON&acc=%ACC</code>
                    </span>
                  </div>
                )}
                {rastreo?.error && <p style={styles.error}>{rastreo.error}</p>}
              </div>
            )}
            {err && <p style={styles.error}>{err}</p>}
            <div style={styles.actions}>
              <Boton variante="ghost" type="button" onClick={() => setModal(null)}>Cancelar</Boton>
              <Boton variante="primary" type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Boton>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

const styles = {
  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  label: { display: 'block', fontWeight: '600', fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem' },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9375rem', boxSizing: 'border-box' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' },
  error: { color: '#dc2626', fontSize: '0.875rem', margin: 0 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' },
  hint: { fontSize: '0.8rem', color: '#64748b', margin: '0 0 0.5rem' },
  linkBtn: { border: 'none', background: 'none', padding: 0, color: '#2563eb', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' },
  vinculoBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.6rem 0.75rem' },
  vinculoOk: { marginTop: '0.5rem', background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.85rem' },
}
